import analytics from '../utils/analytics'
import { SocialFeed } from './Footer'

export function AppGuide() {
  // Handle venue map click tracking
  const handleVenueClick = () => {
    analytics.venueMapClick()
  }

  // Check if dev mode is enabled
  const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'hacker'

  // Check if event has ended (May 30, 2025 was the event date)
  const eventEndTime = new Date('2025-05-30T18:00:00-04:00') // 6 PM ET end time
  const currentTime = new Date()
  const hasEventEnded = currentTime > eventEndTime

  // Handle peak time simulation link click
  const handlePeakTimeClick = () => {
    analytics.trackEvent('post_event_peak_simulation_click', {
      event_category: 'post_event_engagement',
      event_label: 'peak_time_simulation'
    })
  }

  // If event has ended AND not in dev mode, show celebration message
  if (hasEventEnded && !isDevMode) {
    return (
      <div className="bg-gradient-to-br from-editions-orange/20 to-editions-purple/20 border border-editions-orange/40 rounded-xl p-6 mb-6 mx-4 mt-4">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-white mb-3 flex items-center justify-center gap-2">
            🎉 Shopify Editions .DEV Toronto 2025 - What an Event!
          </h2>
          <p className="text-gray-200 text-base mb-4">
            Thank you for being part of an incredible day of innovation, learning, and community! 
            The energy was amazing and the sessions were packed with insights.
          </p>
          
          <div className="bg-editions-dark-light/50 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-semibold text-editions-orange mb-2">
              🕚 Want to Experience the Peak Energy?
            </h3>
            <p className="text-gray-300 text-sm mb-3">
              Curious how busy it was during the peak time around 11 AM? 
              Relive the excitement and see the live dashboard in action!
            </p>
            <a
              href={`${window.location.origin}${window.location.pathname}?dev=hacker&simulate=peak`}
              onClick={handlePeakTimeClick}
              className="inline-flex items-center gap-2 bg-editions-orange/20 text-editions-orange border border-editions-orange/40 px-4 py-2 rounded-lg hover:bg-editions-orange/30 transition-colors text-sm font-medium"
            >
              🚀 Experience Peak Time (11 AM)
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <p className="text-xs text-gray-400 mt-2">
              This will activate dev mode and simulate the busiest time of the event
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-editions-green/10 border border-editions-green/30 rounded-lg p-3">
              <div className="text-editions-green text-lg mb-1">🎯</div>
              <div className="text-white font-medium">Amazing Sessions</div>
              <div className="text-gray-300 text-xs">Cutting-edge insights shared</div>
            </div>
            
            <div className="bg-editions-purple/10 border border-editions-purple/30 rounded-lg p-3">
              <div className="text-editions-purple-light text-lg mb-1">🤝</div>
              <div className="text-white font-medium">Great Networking</div>
              <div className="text-gray-300 text-xs">Connections that matter</div>
            </div>
            
            <div className="bg-editions-orange/10 border border-editions-orange/30 rounded-lg p-3">
              <div className="text-editions-orange text-lg mb-1">💡</div>
              <div className="text-white font-medium">Innovation Unleashed</div>
              <div className="text-gray-300 text-xs">Future of commerce revealed</div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              Keep this app as a memento of an incredible day! 📱✨
            </p>
          </div>
        </div>
        
        {/* Social Feed after the memento text */}
        <SocialFeed />
      </div>
    )
  }

  // Show normal Quick App Guide (during event OR when in dev mode)
  return (
    <div className="bg-gradient-to-br from-editions-purple/20 to-editions-green/10 border border-editions-purple/30 rounded-xl p-6 mb-6 mx-4 mt-4">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center justify-center gap-2">
          🚀 Quick App Guide
          {isDevMode && <span className="text-xs text-editions-orange">(Dev Mode)</span>}
        </h2>
        <p className="text-gray-300 text-sm">
          Make the most of your Editions experience
        </p>
        
        {/* Event Location */}
        <div className="mt-3 mb-4">
          <a 
            href="https://maps.app.goo.gl/9iaiPUQBAYS7VEqw7" 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={handleVenueClick}
            className="inline-flex items-center gap-2 bg-editions-green/20 text-editions-green-light border border-editions-green/30 px-4 py-2 rounded-lg hover:bg-editions-green/30 transition-colors text-sm font-medium"
          >
            📍 Automotive Building, Toronto
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-1M14 1h6m0 0v6m0-6L10 11" />
            </svg>
          </a>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-3">
          <span className="text-editions-orange text-lg">⭐</span>
          <span className="text-gray-200">Star sessions to build your personal plan</span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-editions-purple-light text-lg">📅</span>
          <span className="text-gray-200">Export sessions to your device calendar</span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-editions-green-light text-lg">👤</span>
          <span className="text-gray-200">Click speakers to see all their sessions</span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-gray-300 text-lg">🔍</span>
          <span className="text-gray-200">Use filters to find sessions by topic or speaker</span>
        </div>
      </div>
    </div>
  )
} 